import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  PlayIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { examApi } from '../services/exam.service';
import {
  GuestExamAccess,
  GuestExamAttemptResult,
  PublicDashboardExam,
  Question,
} from '../types/exam';

const formatDate = (value?: string) => {
  if (!value) return 'Not scheduled';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

type ExamOption = NonNullable<Question['options']>[number];

const getExamOptionIdentity = (option: ExamOption, index: number) =>
  String(option.id || option.optionId || option.key || option.text || index);

const getExamOptionAnswerValue = (option: ExamOption, index: number) =>
  String(option.text || option.id || option.optionId || option.key || index);

const isExamOptionSelected = (savedAnswer: string | undefined, option: ExamOption, index: number) => {
  if (!savedAnswer) return false;
  return savedAnswer === getExamOptionAnswerValue(option, index) ||
    savedAnswer === getExamOptionIdentity(option, index) ||
    savedAnswer === String(index);
};

const ExamDetailsPage: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [exam, setExam] = useState<PublicDashboardExam | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  
  // Guest access states
  const [schoolForm, setSchoolForm] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    accessCode: '',
  });
  const [guestAccess, setGuestAccess] = useState<GuestExamAccess | null>(null);
  const [guestAttempt, setGuestAttempt] = useState<GuestExamAttemptResult | null>(null);
  const [guestQuestions, setGuestQuestions] = useState<Question[]>([]);
  const [guestAnswers, setGuestAnswers] = useState<Record<string, string>>({});
  const [studentInvite, setStudentInvite] = useState<{ shareUrl: string; inviteCode: string } | null>(null);
  
  // Timer and progress tracking
  const [examStartTime, setExamStartTime] = useState<Date | null>(null);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);
  
  // Security features
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isExamInvalidated, setIsExamInvalidated] = useState(false);
  const [warningCount, setWarningCount] = useState(0);
  const [lastActiveTime, setLastActiveTime] = useState(Date.now());
  const examDuration = Math.max(1, Number(exam?.duration || 45)) * 60;

  const registrationLink = '/register';

  useEffect(() => {
    if (!examId) {
      navigate('/exam-dashboard');
      return;
    }

    const loadExam = async () => {
      try {
        setLoading(true);
        const response = await examApi.getPublicDashboardExamById(examId);
        setExam(response);
      } catch (error) {
        console.error('Failed to load exam detail:', error);
        navigate('/exam-dashboard');
      } finally {
        setLoading(false);
      }
    };

    void loadExam();
  }, [examId, navigate]);

  useEffect(() => {
    if (!guestAttempt?.attemptId || !guestAttempt?.accessToken) return;
    const loadQuestions = async () => {
      try {
        const questions = await examApi.getGuestAttemptQuestions(
          guestAttempt.attemptId,
          guestAttempt.accessToken,
        );
        setGuestQuestions(Array.isArray(questions) ? questions : []);
      } catch (error) {
        console.error('Failed to load guest attempt questions:', error);
      }
    };
    void loadQuestions();
  }, [guestAttempt?.attemptId, guestAttempt?.accessToken]);

  
  // Start timer when exam begins
  useEffect(() => {
    if (guestAttempt && guestAttempt.status !== 'GRADED' && !examStartTime) {
      setExamStartTime(new Date());
      setLastActiveTime(Date.now());
    }
  }, [guestAttempt, examStartTime]);

  useEffect(() => {
    if (!guestAttempt) {
      setTimeRemaining(examDuration);
    }
  }, [examDuration, guestAttempt]);

  const handleExamInvalidation = React.useCallback((reason: string) => {
    console.warn('Exam invalidated:', reason);
    // You could also call an API to log the invalidation
    if (timerInterval) {
      clearInterval(timerInterval);
    }
  }, [timerInterval]);

  const submitGuestAttempt = React.useCallback(async () => {
    if (!guestAttempt?.attemptId || !guestAttempt.accessToken) return;
    try {
      setBusy(true);
      const result = await examApi.submitGuestAttempt(guestAttempt.attemptId, guestAttempt.accessToken, {
        totalTimeSpent: 0,
      });
      setGuestAttempt(result);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to submit guest attempt');
    } finally {
      setBusy(false);
    }
  }, [guestAttempt?.attemptId, guestAttempt?.accessToken]);

  // Timer management - Countdown from 45 minutes
  useEffect(() => {
    if (guestAttempt && guestAttempt.status !== 'GRADED' && examStartTime && !isExamInvalidated) {
      const interval = setInterval(() => {
        const elapsed = Math.floor((new Date().getTime() - examStartTime.getTime()) / 1000);
        const remaining = Math.max(0, examDuration - elapsed);
        
        setTimeRemaining(remaining);
        
        // Auto-submit when time is up
        if (remaining === 0 && !isExamInvalidated) {
          setIsExamInvalidated(true);
          handleExamInvalidation('Time expired');
          // Auto-submit exam
          void submitGuestAttempt();
        }
      }, 1000);
      setTimerInterval(interval);
      
      return () => clearInterval(interval);
    } else if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  }, [guestAttempt, examStartTime, isExamInvalidated, examDuration, handleExamInvalidation, submitGuestAttempt, timerInterval]);

  // Security monitoring - Tab switching and page visibility
  useEffect(() => {
    if (!guestAttempt || guestAttempt.status === 'GRADED' || isExamInvalidated) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User switched tab or minimized window
        setWarningCount(prev => prev + 1);
        
        // Invalidate exam after 2 warnings
        if (warningCount >= 1) {
          setIsExamInvalidated(true);
          handleExamInvalidation('Tab switching detected multiple times');
        }
      } else {
        setLastActiveTime(Date.now());
      }
    };

    const handleBlur = () => {
      if (!isExamInvalidated) {
        setWarningCount(prev => prev + 1);
        
        if (warningCount >= 1) {
          setIsExamInvalidated(true);
          handleExamInvalidation('Window focus lost - possible malpractice');
        }
      }
    };

    const handleFocus = () => {
      setLastActiveTime(Date.now());
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isExamInvalidated && guestAttempt?.status !== 'GRADED') {
        e.preventDefault();
        e.returnValue = 'Leaving this page will invalidate your exam. Are you sure?';
        return e.returnValue;
      }
    };

    // Check for inactivity
    const inactivityInterval = setInterval(() => {
      const currentTime = Date.now();
      const inactiveTime = Math.floor((currentTime - lastActiveTime) / 1000);
      
      if (inactiveTime > 60) { // 1 minute of inactivity
        setIsExamInvalidated(true);
        handleExamInvalidation('Extended inactivity detected');
      }
    }, 30000); // Check every 30 seconds

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(inactivityInterval);
    };
  }, [guestAttempt, isExamInvalidated, warningCount, lastActiveTime, handleExamInvalidation, timerInterval]);

  // Progress calculations
  const getProgressStats = () => {
    const totalQuestions = guestQuestions.length;
    const answeredQuestions = Object.keys(guestAnswers).length;
    const remainingQuestions = totalQuestions - answeredQuestions;
    const progressPercentage = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
    
    return {
      total: totalQuestions,
      answered: answeredQuestions,
      remaining: remainingQuestions,
      percentage: Math.round(progressPercentage)
    };
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const copy = async (text: string, message: string) => {
    await navigator.clipboard.writeText(text);
    window.alert(message);
  };

  const createStudentInvite = async () => {
    if (!exam?.examId) return;
    try {
      setBusy(true);
      const response = await examApi.createReferralInvite(exam.examId);
      setStudentInvite({ shareUrl: response.shareUrl, inviteCode: response.inviteCode });
      await copy(response.shareUrl, 'Referral exam invite copied.');
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to create referral invite');
    } finally {
      setBusy(false);
    }
  };

  const unlockViaSchoolCode = async () => {
    if (!exam?.examId) return;
    try {
      setBusy(true);
      const access = await examApi.createSchoolGuestAccess(exam.examId, schoolForm);
      setGuestAccess(access);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to validate school access');
    } finally {
      setBusy(false);
    }
  };

  const startGuestAttempt = async () => {
    if (!exam?.examId || !guestAccess?.accessToken) return;
    try {
      setBusy(true);
      const attempt = await examApi.startGuestAttempt(exam.examId, guestAccess.accessToken);
      setGuestAttempt(attempt);
    } catch (error: any) {
      window.alert(error?.response?.data?.message || 'Failed to start exam attempt');
    } finally {
      setBusy(false);
    }
  };

  const saveGuestAnswer = async (questionId: string, answer: string) => {
    if (!guestAttempt?.attemptId || !guestAttempt.accessToken) return;
    setGuestAnswers((prev) => ({ ...prev, [questionId]: answer }));
    try {
      await examApi.saveGuestAttemptAnswer(guestAttempt.attemptId, guestAttempt.accessToken, {
        questionId,
        answer,
        timeSpent: 0,
      });
    } catch (error) {
      console.error('Failed to save guest answer:', error);
    }
  };

  
  if (loading) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,_#f7f4ea_0%,_#fffdf8_36%,_#f3efe2_100%)] text-slate-900">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
            Loading exam details...
          </div>
        </div>
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="min-h-screen bg-[linear-gradient(180deg,_#f7f4ea_0%,_#fffdf8_36%,_#f3efe2_100%)] text-slate-900">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-16 text-center text-sm text-slate-500">
            Exam not found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f7f4ea_0%,_#fffdf8_36%,_#f3efe2_100%)] text-slate-900">
      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/exam-dashboard"
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to Dashboard
          </Link>
        </div>

        {/* Exam Header */}
        <section className="mb-8 overflow-hidden rounded-[2.5rem] border border-amber-200 bg-[radial-gradient(circle_at_top_right,_rgba(245,158,11,0.22),_transparent_28%),linear-gradient(135deg,_#111827_0%,_#1f2937_42%,_#78350f_100%)] p-8 text-white shadow-[0_32px_90px_-48px_rgba(17,24,39,0.85)]">
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-900">
              {exam.publicDashboardCategory.replace('_', ' ')}
            </span>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
              {exam.subject}
            </span>
            {exam.schoolAccessEnabled ? (
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                School code enabled
              </span>
            ) : null}
            {exam.guestReferralEnabled ? (
              <span className="rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-800">
                Referral fee INR {Number(exam.guestAttemptFee || 0).toFixed(2)}
              </span>
            ) : null}
          </div>
          <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">{exam.title}</h1>
          <p className="mt-2 text-sm text-amber-50">
            {exam.publicDashboardOrganizer} | Starts {formatDate(exam.startDate)}
          </p>
          <p className="mt-4 max-w-3xl text-sm leading-6 text-amber-50 sm:text-base">
            {exam.description}
          </p>
        </section>

        {/* Responsive Layout */}
        <div className="grid gap-8 lg:grid-cols-[1fr_400px] xl:grid-cols-[1.2fr_400px]">
          {/* Main Content - Questions and Attempt */}
          <div className="space-y-6">
            {guestAttempt ? (
              <div className="space-y-4 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
                {/* Timer and Progress Bar */}
                {guestAttempt.status !== 'GRADED' && (
                  <div className={`space-y-4 rounded-[1.25rem] border p-4 ${
                    isExamInvalidated 
                      ? 'border-red-200 bg-red-50' 
                      : timeRemaining <= 300 // Less than 5 minutes
                        ? 'border-orange-200 bg-orange-50'
                        : 'border-blue-200 bg-blue-50'
                  }`}>
                    {/* Exam Invalidated Warning */}
                    {isExamInvalidated && (
                      <div className="rounded-lg border border-red-300 bg-red-100 p-3 text-center">
                        <div className="text-lg font-bold text-red-800">⚠️ EXAM INVALIDATED</div>
                        <div className="text-sm text-red-700">Malpractice detected. Your exam has been automatically submitted.</div>
                      </div>
                    )}
                    
                    {/* Tab Switching Warning */}
                    {warningCount > 0 && !isExamInvalidated && (
                      <div className="rounded-lg border border-yellow-300 bg-yellow-100 p-3 text-center">
                        <div className="text-sm font-bold text-yellow-800">⚠️ WARNING: Tab switching detected!</div>
                        <div className="text-xs text-yellow-700">One more warning will invalidate your exam.</div>
                      </div>
                    )}
                    
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`rounded-full px-3 py-1.5 text-sm font-semibold text-white ${
                          isExamInvalidated 
                            ? 'bg-red-600'
                            : timeRemaining <= 300
                              ? 'bg-orange-600'
                              : 'bg-blue-600'
                        }`}>
                          ⏱️ {formatTime(timeRemaining)}
                        </div>
                        <div className={`text-sm ${
                          isExamInvalidated 
                            ? 'text-red-800'
                            : timeRemaining <= 300
                              ? 'text-orange-800'
                              : 'text-blue-800'
                        }`}>
                          Time Remaining
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-blue-800">
                          Progress: {getProgressStats().answered}/{getProgressStats().total}
                        </div>
                        <div className="rounded-full bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white">
                          {getProgressStats().percentage}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="w-full rounded-full bg-blue-200 h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                        style={{ width: `${getProgressStats().percentage}%` }}
                      />
                    </div>
                    
                    {/* Question Stats */}
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div className="rounded-lg bg-white p-2">
                        <div className="text-lg font-bold text-blue-600">{getProgressStats().total}</div>
                        <div className="text-xs text-blue-800">Total Questions</div>
                      </div>
                      <div className="rounded-lg bg-green-50 p-2">
                        <div className="text-lg font-bold text-green-600">{getProgressStats().answered}</div>
                        <div className="text-xs text-green-800">Answered</div>
                      </div>
                      <div className="rounded-lg bg-orange-50 p-2">
                        <div className="text-lg font-bold text-orange-600">{getProgressStats().remaining}</div>
                        <div className="text-xs text-orange-800">Remaining</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Status and Score */}
                <div className="flex flex-wrap gap-3 text-sm text-slate-600">
                  <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">Status: {guestAttempt.status}</span>
                  {guestAttempt.status === 'GRADED' && guestAttempt.resultsPublished === false ? (
                    <span className="rounded-full bg-amber-100 px-3 py-1 font-semibold text-amber-800">Result pending publication</span>
                  ) : (
                    <>
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">Score: {guestAttempt.totalScore}</span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">Percent: {guestAttempt.percentageScore}%</span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 font-semibold">Grade: {guestAttempt.grade || '-'}</span>
                    </>
                  )}
                </div>
                
                {guestAttempt.status !== 'GRADED' ? (
                  <>
                    {/* Responsive Questions Layout */}
                    <div className="space-y-4">
                      {guestQuestions.map((question) => {
                        const isAnswered = !!guestAnswers[question.questionId];
                        return (
                          <div 
                            key={question.questionId} 
                            className={`rounded-[1.25rem] border p-4 transition-all duration-200 ${
                              isAnswered 
                                ? 'border-green-300 bg-green-50' 
                                : 'border-slate-200 bg-slate-50'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`mt-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                                isAnswered 
                                  ? 'bg-green-600 text-white' 
                                  : 'bg-slate-300 text-slate-600'
                              }`}>
                                {isAnswered ? '✓' : question.sequenceNumber}
                              </div>
                              <div className="flex-1">
                                <p className={`text-sm font-semibold ${
                                  isAnswered ? 'text-green-900' : 'text-slate-950'
                                }`}>
                                  Q{question.sequenceNumber}. {question.questionText}
                                </p>
                                {/* Mobile: Vertical options, Web: Horizontal options */}
                                <div className="mt-3 space-y-2 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-2">
                                  {(question.options || []).map((option, index) => {
                                    const optionLetter = String.fromCharCode(65 + index); // A, B, C, D...
                                    const optionValue = getExamOptionAnswerValue(option, index);
                                    const isSelected = isExamOptionSelected(
                                      guestAnswers[question.questionId],
                                      option,
                                      index,
                                    );
                                    const isDisabled = isExamInvalidated || guestAttempt.status === 'GRADED';
                                    return (
                                      <label 
                                        key={getExamOptionIdentity(option, index)} 
                                        className={`flex items-center gap-3 text-sm rounded-lg p-3 transition-colors ${
                                          isDisabled
                                            ? 'cursor-not-allowed opacity-60'
                                            : 'cursor-pointer'
                                        } ${
                                          isSelected
                                            ? 'bg-green-100 text-green-800 border border-green-300'
                                            : isDisabled
                                              ? 'bg-gray-50 text-gray-500 border border-gray-200'
                                              : 'text-slate-700 hover:bg-slate-100 border border-transparent'
                                        }`}
                                      >
                                        <input
                                          type="radio"
                                          name={question.questionId}
                                          value={optionValue}
                                          checked={isSelected}
                                          onChange={() => !isDisabled && void saveGuestAnswer(question.questionId, optionValue)}
                                          disabled={isDisabled}
                                          className="sr-only"
                                        />
                                        <div className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold border-2 ${
                                          isSelected
                                            ? 'bg-green-600 text-white border-green-600'
                                            : isDisabled
                                              ? 'bg-gray-300 text-gray-500 border-gray-300'
                                              : 'bg-white text-slate-600 border-slate-300'
                                        }`}>
                                          {optionLetter}
                                        </div>
                                        <span className="flex-1">{option.text}</span>
                                        {isSelected && (
                                          <div className={`h-5 w-5 rounded-full flex items-center justify-center ${
                                            isDisabled
                                              ? 'bg-gray-400'
                                              : 'bg-green-600'
                                          }`}>
                                            <svg className="h-3 w-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                          </div>
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {guestQuestions.length > 0 ? (
                      <button
                        onClick={() => void submitGuestAttempt()}
                        disabled={busy || isExamInvalidated}
                        className={`w-full rounded-full px-4 py-2.5 text-sm font-semibold lg:w-auto ${
                          isExamInvalidated
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-slate-950 text-white hover:bg-slate-800'
                        }`}
                      >
                        {isExamInvalidated 
                          ? 'Exam Invalidated' 
                          : busy 
                            ? 'Submitting...' 
                            : 'Submit exam'
                        }
                      </button>
                    ) : (
                      <p className="text-sm text-slate-500">Loading questions...</p>
                    )}
                  </>
                ) : (
                  <div className={`rounded-[1.25rem] border p-4 text-sm ${
                    guestAttempt.resultsPublished === false
                      ? 'border-amber-200 bg-amber-50 text-amber-800'
                      : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                  }`}>
                    {guestAttempt.resultsPublished === false
                      ? 'Your attempt has been submitted. Results will appear after the teacher publishes them.'
                      : `Result ready. ${guestAttempt.isPassed ? 'Passed' : 'Not passed'} with ${guestAttempt.percentageScore}%.`}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
                <h2 className="text-lg font-semibold tracking-tight text-slate-950">Question Preview</h2>
                {!exam.questionsPreview?.length ? (
                  <p className="mt-3 text-sm text-slate-500">Question preview is not available for this exam.</p>
                ) : (
                  <div className="mt-4 space-y-3">
                    {/* Mobile: Vertical, Web: Horizontal */}
                    <div className="space-y-3 lg:grid lg:grid-cols-2 lg:gap-4 lg:space-y-0">
                      {exam.questionsPreview.map((question) => (
                        <div key={question.questionId} className="rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-3">
                          <p className="text-sm font-semibold text-slate-950">{question.questionText}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            {question.questionType} | {question.marks} marks | {question.difficulty || 'Unspecified difficulty'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar - Access Controls */}
          <div className="space-y-6">
            {/* Access Workspace */}
            <div className="rounded-[2rem] border border-slate-200 bg-white shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
              <div className="border-b border-slate-200 px-6 py-5">
                <h2 className="text-lg font-semibold tracking-tight text-slate-950">Exam Access</h2>
                <p className="mt-1 text-sm text-slate-500">Unlock and attempt this exam</p>
              </div>
              <div className="space-y-5 px-6 py-5">
                {user?.role === 'STUDENT' && exam.guestReferralEnabled ? (
                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Student referral invite</p>
                    <p className="mt-3 text-sm text-slate-600">
                      Create a paid one-time exam invite for a friend. This does not register them in the platform.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        onClick={() => void createStudentInvite()}
                        disabled={busy}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                        {busy ? 'Generating...' : 'Generate referral invite'}
                      </button>
                      {studentInvite ? (
                        <button
                          onClick={() => void copy(studentInvite.shareUrl, 'Referral invite copied.')}
                          className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"
                        >
                          Copy invite again
                        </button>
                      ) : null}
                    </div>
                    {studentInvite ? (
                      <p className="mt-3 text-xs text-slate-500">Invite code: {studentInvite.inviteCode}</p>
                    ) : null}
                  </div>
                ) : null}

                {!guestAttempt && !guestAccess && exam.schoolAccessEnabled ? (
                  <div className="rounded-[1.5rem] border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">School access</p>
                    <div className="mt-4 grid gap-3">
                      <input
                        value={schoolForm.guestName}
                        onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestName: e.target.value }))}
                        placeholder="Candidate name"
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                      <input
                        value={schoolForm.guestEmail}
                        onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestEmail: e.target.value }))}
                        placeholder="Email (optional)"
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                      <input
                        value={schoolForm.guestPhone}
                        onChange={(e) => setSchoolForm((prev) => ({ ...prev, guestPhone: e.target.value }))}
                        placeholder="Phone (optional)"
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                      <input
                        value={schoolForm.accessCode}
                        onChange={(e) => setSchoolForm((prev) => ({ ...prev, accessCode: e.target.value.toUpperCase() }))}
                        placeholder="Enter common school code"
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                      <button
                        onClick={() => void unlockViaSchoolCode()}
                        disabled={busy}
                        className="rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                      >
                        {busy ? 'Validating...' : 'Unlock via school code'}
                      </button>
                    </div>
                  </div>
                ) : null}

                {guestAccess && !guestAttempt ? (
                  <div className="rounded-[1.5rem] border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-sm font-semibold text-emerald-900">
                      Access unlocked for {guestAccess.guestName || 'candidate'}.
                    </p>
                    <p className="mt-2 text-sm text-emerald-700">
                      Mode: {guestAccess.accessType === 'SCHOOL_CODE' ? 'School code' : 'Paid referral'}.
                    </p>
                    <button
                      onClick={() => void startGuestAttempt()}
                      disabled={busy}
                      className="mt-4 inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white"
                    >
                      <PlayIcon className="h-4 w-4" />
                      {busy ? 'Starting...' : 'Start exam'}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Platform Access */}
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
              <p className="text-xs uppercase tracking-[0.18em] text-slate-400">Platform access</p>
              <p className="mt-3 text-sm text-slate-700">
                Exam guest access is separate from application registration. Use the main registration flow only if you want classes or the rest of the platform.
              </p>
              <Link
                to={registrationLink}
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open platform registration
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExamDetailsPage;
